/**
 * Панель текущего звонка — центр пульта.
 *
 * «Разговор» и таймер включаются ТОЛЬКО по подтверждению станции, что абонент
 * снял трубку (событие call.answered от ноги транка, §7.4). SIP-ответ браузеру
 * здесь не значит ничего: станция поднимает ногу диспетчера в первую же
 * секунду набора — ради гудков. Пока подтверждения нет, панель честно пишет
 * «Идут гудки», и время разговора не тикает. Запрос диспетчеров 29.08.2026:
 * отсчёт секунд — только с момента, когда человек снял трубку.
 *
 * Панель одна на все звонки — и по карточке, и набранные руками. Второй копии
 * быть не должно: две панели разойдутся по таймеру и по состоянию кнопок,
 * и диспетчер будет жать «Завершить» у той, что уже не управляет сессией.
 *
 * Пульс — только у живого разговора. Это единственная анимация пульта:
 * на экране, на который смотрят восемь часов, мигать может только то,
 * что требует внимания прямо сейчас.
 */

import { useEffect, useState } from 'react';
import { Mic, MicOff, PhoneOff, X } from 'lucide-react';
import type { SoftphoneCallSnapshot } from '@cc/softphone-core';

import { Button } from '../../shared/ui/button.js';
import { formatDuration, elapsedSeconds } from '../../shared/lib/format-duration.js';
import { formatPhone } from '../../shared/lib/format-phone.js';
import { isManualCall } from '../softphone/manual-dial.js';

export interface CallPanelProps {
  call: SoftphoneCallSnapshot;
  /** Имя из карточки; у ручного набора его нет */
  contactName?: string | null;
  /**
   * Момент, когда станция подтвердила ответ абонента, — по часам ЭТОГО
   * браузера на приход события, а не серверная отметка: часы клиента и
   * сервера расходятся сильнее, чем задержка доставки. null — ещё не ответил.
   */
  answeredAtClient: number | null;
  onHangup: () => void;
  onToggleMute: (muted: boolean) => void;
  /**
   * Убрать завершённый звонок с пульта. Задаётся только для ручного набора:
   * звонок по карточке с пульта убирает метка результата, и вторая кнопка
   * «Закрыть» рядом с метками означала бы «закрыть разговор без результата».
   */
  onDismiss?: () => void;
}

/** Что показать диспетчеру: SIP-состояние само по себе про разговор не говорит */
function callPhase(
  call: SoftphoneCallSnapshot,
  answeredAtClient: number | null,
): { title: string; dot: string; talking: boolean } {
  if (call.state === 'ended') {
    return { title: 'Звонок завершён', dot: 'bg-slate-400', talking: false };
  }
  if (answeredAtClient !== null) {
    return { title: 'Разговор', dot: 'bg-emerald-500', talking: true };
  }
  if (call.state === 'dialing') {
    return { title: 'Набираю', dot: 'bg-amber-500', talking: false };
  }
  // SIP уже установился, но абонент трубку не снял — станция играет гудки
  return { title: 'Идут гудки', dot: 'bg-amber-500', talking: false };
}

export function CallPanel({
  call,
  contactName,
  answeredAtClient,
  onHangup,
  onToggleMute,
  onDismiss,
}: CallPanelProps): React.JSX.Element {
  const phase = callPhase(call, answeredAtClient);
  const seconds = useTickingSeconds(answeredAtClient, phase.talking);
  const manual = isManualCall(call);
  const live = call.state !== 'ended';

  return (
    <section
      className={
        'flex flex-col items-center gap-4 rounded-2xl border bg-white px-6 py-7 text-center ' +
        (phase.talking ? 'border-blue-200 animate-call-pulse' : 'border-slate-200')
      }
    >
      {/* Карусель соединила с живым человеком: без этого баннера авто-снятая
          трубка выглядит как обычный набор, и диспетчер молчит первые секунды */}
      {call.isCarousel && live ? (
        <p className="w-full rounded-xl bg-emerald-600 px-4 py-2.5 text-base font-extrabold text-white">
          Соединение — говорите!
        </p>
      ) : null}
      <span className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-sm font-semibold text-slate-700">
        <span
          className={`h-2 w-2 rounded-full ${phase.dot} ${phase.talking ? 'animate-pulse' : ''}`}
          aria-hidden
        />
        {phase.title}
        {/* Подпись обязательна: без неё завершённый ручной звонок неотличим от
            звонка по карточке, и диспетчер ищет, какую метку ему поставить */}
        {manual ? ' · ручной набор, карточки нет' : ''}
      </span>

      <div className="flex flex-col gap-1">
        <p className="text-3xl font-extrabold tabular-nums tracking-tight text-slate-900">
          {formatPhone(call.phone)}
        </p>
        {contactName != null && contactName !== '' ? (
          <p className="text-base text-slate-500">{contactName}</p>
        ) : null}
      </div>

      {/* До ответа таймера нет вовсе: нули, тикающие под гудки, читаются как
          разговор — ровно от этого и уходим. Итоговую длительность считает
          сервер по мосту, панель после отбоя её не изображает */}
      {phase.talking ? (
        <p className="text-5xl font-extrabold tabular-nums text-slate-900">
          {formatDuration(seconds)}
        </p>
      ) : (
        <p className="text-5xl font-extrabold tabular-nums text-slate-300">—:—</p>
      )}

      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button
          variant={call.muted ? 'primary' : 'secondary'}
          size="lg"
          onClick={() => {
            onToggleMute(!call.muted);
          }}
          disabled={!live}
        >
          {call.muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          {call.muted ? 'Включить микрофон' : 'Выключить микрофон'}
        </Button>

        {live ? (
          <Button variant="danger" size="lg" onClick={onHangup}>
            <PhoneOff className="h-5 w-5" />
            Завершить
          </Button>
        ) : null}

        {onDismiss !== undefined && !live ? (
          <Button variant="secondary" size="lg" onClick={onDismiss}>
            <X className="h-5 w-5" />
            Закрыть
          </Button>
        ) : null}
      </div>
    </section>
  );
}

/** Секунды разговора, пересчёт раз в секунду и только пока разговор идёт */
function useTickingSeconds(answeredAt: number | null, running: boolean): number {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (answeredAt === null || !running) {
      setSeconds(0);
      return;
    }
    setSeconds(elapsedSeconds(answeredAt, Date.now()));
    const id = setInterval(() => {
      setSeconds(elapsedSeconds(answeredAt, Date.now()));
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, [answeredAt, running]);

  return seconds;
}
